import { DATE_LOCALE, DICTS, type Lang } from "./i18n";

/**
 * How times and people are written in the console.
 *
 * Short and spoken: "4 dk" / "4m", not "4 minutes ago" — the list is scanned,
 * not read, and every character competes with the visitor's name for space.
 */

const MINUTE = 60_000;

export function ago(iso: string, now: number = Date.now(), lang: Lang = "tr"): string {
  const t = DICTS[lang].time;
  const minutes = Math.floor((now - new Date(iso).getTime()) / MINUTE);
  if (minutes < 1) return t.now;
  if (minutes < 60) return t.minutes(minutes);
  if (minutes < 60 * 24) return t.hours(Math.floor(minutes / 60));
  return t.days(Math.floor(minutes / (60 * 24)));
}

/** "4 dk önce" / "4m ago", but plain "şimdi" / "just now" under a minute. */
export function agoPhrase(iso: string, now: number = Date.now(), lang: Lang = "tr"): string {
  const t = DICTS[lang].time;
  const short = ago(iso, now, lang);
  return short === t.now ? t.now : t.ago(short);
}

export function clock(iso: string, lang: Lang = "tr"): string {
  return new Date(iso).toLocaleTimeString(DATE_LOCALE[lang], { hour: "2-digit", minute: "2-digit" });
}

export function dateTime(iso: string, lang: Lang = "tr"): string {
  return new Date(iso).toLocaleString(DATE_LOCALE[lang], {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function longDate(iso: string, lang: Lang = "tr"): string {
  return new Date(iso).toLocaleDateString(DATE_LOCALE[lang], { day: "numeric", month: "long", year: "numeric" });
}

function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function dayLabel(iso: string, now: number = Date.now(), lang: Lang = "tr"): string {
  const t = DICTS[lang].time;
  const day = startOfDay(new Date(iso).getTime());
  const today = startOfDay(now);
  if (day === today) return t.today;
  if (day === startOfDay(today - 12 * 60 * MINUTE)) return t.yesterday;
  return longDate(iso, lang);
}

export type WaitHeat = "none" | "fresh" | "warm" | "hot";

/**
 * How urgently a waiting visitor needs an answer. Two minutes is a normal
 * reply time for chat; five is where people start to leave.
 */
export function waitHeat(waitingSince: string | null, now: number = Date.now()): WaitHeat {
  if (!waitingSince) return "none";
  const minutes = (now - new Date(waitingSince).getTime()) / MINUTE;
  if (minutes < 2) return "fresh";
  if (minutes < 5) return "warm";
  return "hot";
}

export function visitorLabel(
  row: { visitorName: string | null; visitorEmail: string | null; visitorId: number },
  lang: Lang = "tr",
): string {
  return row.visitorName || row.visitorEmail || DICTS[lang].visitor(row.visitorId);
}

/**
 * What an audit row was done to: "operator: Agent X (#7)".
 *
 * The name comes from the snapshot the server took when it wrote the row, not
 * from a lookup — the target may have been renamed or deleted since, and the
 * log has to keep saying what was true at the time. Rows written before the
 * snapshot existed, and rows about a visitor who has since asked to be erased,
 * carry no name at all and fall back to the id.
 */
export function auditTarget(
  entry: { targetType: string | null; targetId: string | null; metadata?: Record<string, unknown> | null },
  lang: Lang = "tr",
): string | null {
  if (!entry.targetType || !entry.targetId) return null;
  const metadata = entry.metadata ?? {};
  const pick = (key: string): string | null => {
    const value = metadata[key];
    return typeof value === "string" && value.trim() ? value.trim() : null;
  };
  return DICTS[lang].audit.target(entry.targetType, entry.targetId, pick("targetName") ?? pick("targetEmail"));
}

export function statusLabel(status: string, lang: Lang = "tr"): string {
  const labels = DICTS[lang].status as Record<string, string>;
  return labels[status] ?? status;
}

/** A duration in seconds as "45 sn" / "12 dk" / "2 sa 5 dk". */
export function duration(seconds: number | null, lang: Lang = "tr"): string {
  const t = DICTS[lang].stats;
  if (seconds === null || !Number.isFinite(seconds)) return t.none;
  if (seconds < 60) return t.seconds(Math.round(seconds));
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t.minutes(minutes);
  return t.hoursMinutes(Math.floor(minutes / 60), minutes % 60);
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;

/**
 * A byte count the way an owner reads a disk: "1,5 GB", not 1610612736.
 *
 * Binary steps with the everyday labels, because the number this is shown
 * against — the per-site attachment budget — is itself a power of two.
 */
export function bytes(value: number, lang: Lang = "tr"): string {
  let size = Number.isFinite(value) ? Math.max(0, value) : 0;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const rounded = size.toLocaleString(DATE_LOCALE[lang], {
    // Whole bytes are whole; anything scaled gets one decimal at most, and
    // "1 GB" reads better than "1.0 GB".
    maximumFractionDigits: unit === 0 ? 0 : 1,
  });
  return `${rounded} ${BYTE_UNITS[unit]}`;
}

/**
 * How full a site's attachment storage is, and whether to say so loudly.
 *
 * Four fifths is early enough that deleting a few old conversations still
 * fixes it; past the cap the widget starts refusing images, and by then the
 * first the owner hears of it is a customer complaining.
 */
export function storageUse(usedBytes: number, capBytes: number): { percent: number; warn: boolean } {
  const percent = capBytes > 0 ? Math.min(100, Math.round((usedBytes / capBytes) * 100)) : 100;
  return { percent, warn: percent >= 80 };
}
